import React, { useRef, ReactElement, useCallback, MutableRefObject } from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';

import { AlmeResponseNext, AlmeResponseV5, AlmeResponseV6 } from '@alme/types/response/AlmeResponse';
import { DisplayLink } from '@alme/types/response/DisplayLink';
import { EntryExtensionData } from '@alme/types/response/EntryExtensionData';

import responseStyles from './AlmeResponse.styles';
import { FocusComponentProps } from '../../models/ComponentProps';

import { CtaResponseAction } from '../ResponseActions/AlmeCtaButton/AlmeCtaButton';

import { AlmeApiUtils } from '../../api/AlmeApiUtils';
import { AlmeResponseConfig } from './AlmeResponseConfig';
import AlmeUserResponse from './AlmeUserResponse/AlmeUserResponse';
import AlmeIvaResponse from './AlmeIvaResponse/AlmeIvaResponse';
import { FireSurveyAppEvent } from "../ResponseActions/AlmeSurvey/AlmeSurvey";
import { NavUrl } from '@alme/types/response/NavUrl';
import { ConvertAlmeResponse, onDisplayLinkClick } from './AlmeResponseUtils';

export type AlmeResponseClasses = Record<"userCardContainer" | "displayLink" | "navUrl" | "sectionHeader", string>;
export interface AlmeMarkdownResponseV5 extends AlmeResponseV5 {
    html: string;
}

export interface AlmeMarkdownResponseV6 extends AlmeResponseV6 {
    html: string;
}

export interface AlmeMarkdownResponseNext extends AlmeResponseNext {
    html: string;
}


interface AlmeResponseProps extends WithStyles<typeof responseStyles> {
    response: AlmeResponseNext;
    children?: ReactElement[];
    isLastResponse: boolean;
    onDisplayLinkClick: (link: DisplayLink | NavUrl) => void;
    onSurveySubmitClick: FireSurveyAppEvent;
    almeConfig: AlmeResponseConfig;
    setIsDialogOpen: (isOpen: boolean) => void;
    focusProps?: FocusComponentProps;
    onCtaClick: (action: CtaResponseAction, entryId: string) => Promise<void>;
    responseId?: string | number;
    isDialogOpen?: boolean;
    onScroll: () => void;
    scrollToResponse?: MutableRefObject<boolean>;
}

const AlmeResponse: React.FC<AlmeResponseProps> = (props: AlmeResponseProps) => {
    const response = ConvertAlmeResponse(props.response);

    const responseRevision = (props.responseId || response.responseRevision).toString();
    const entryId = response.id || (response.extensionData && (response.extensionData as EntryExtensionData)?.entryId) || responseRevision;

    const userResponseRef = useRef<HTMLDivElement>(null);
    const agentResponseRef = useRef<HTMLDivElement>(null);

    return (
        <>
            <AlmeUserResponse
                response={response}
                responseRevision={responseRevision}
                entryId={entryId}
                userResponseRef={userResponseRef}
                onScroll={props.onScroll}
                userIcon={props.almeConfig.response.userResponse.userIcon}
                focusProps={props.focusProps}
            />
            <AlmeIvaResponse
                almeConfig={props.almeConfig.response}
                response={response}
                responseRevision={responseRevision}
                isLastResponse={props.isLastResponse}
                entryId={entryId}
                agentResponseRef={agentResponseRef}
                onScroll={props.onScroll}
                onCtaClick={props.onCtaClick}
                setIsDialogOpen={props.setIsDialogOpen}
                onDisplayLinkClick={props.onDisplayLinkClick}
                onSurveySubmitClick={props.onSurveySubmitClick}
                ivaIcon={props.almeConfig.response.ivaResponse.ivaIcon}
                isDialogOpen={props.isDialogOpen}
                focusProps={props.focusProps}
                scrollToResponse={props.scrollToResponse}
            />
        </>
    );
};

export default withStyles(responseStyles)(AlmeResponse);

export function OnDisplayLinkClick(apiUtils: React.MutableRefObject<AlmeApiUtils>, scrollToResponse: MutableRefObject<boolean>): (link: DisplayLink | NavUrl) => Promise<void> {
    return useCallback(async (link: DisplayLink | NavUrl) => {
        scrollToResponse.current = true;
        await onDisplayLinkClick(link, apiUtils);
    }, [apiUtils, scrollToResponse]);
}
